'use client';

import { useEffect } from 'react';

// A stale cached app shell (PWA / browser cache) after a deploy can reference a
// JS chunk the new build no longer serves, which surfaces here as a
// ChunkLoadError. Rather than stranding the user on the error screen, reload
// once to fetch a consistent build. Guarded so it can never loop.
function isChunkLoadError(error: (Error & { digest?: string }) | undefined) {
  const message = `${error?.message ?? ''}`;
  const name = `${error?.name ?? ''}`;
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w./-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /(Failed to fetch|error loading) dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (!isChunkLoadError(error)) {
      return;
    }

    try {
      const key = 'cleanor-chunk-reload-at';
      const last = Number(window.sessionStorage.getItem(key) || '0');
      if (Date.now() - last < 30000) {
        return;
      }
      window.sessionStorage.setItem(key, String(Date.now()));
    } catch {
      // If sessionStorage is unavailable, still attempt one reload below.
    }

    window.location.reload();
  }, [error]);

  return (
    <html lang="en">
      <body>
        <h2>Something went wrong!</h2>
        <button type="button" onClick={() => reset()}>
          Try again
        </button>
      </body>
    </html>
  );
}
